import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Eraser, Paintbrush, Plus, Trash2 } from 'lucide-react';
import { staffApi, AvailabilityResponse, AvailabilityWindow } from './staffApi';

/*
 * A provider's week, painted. Each column is a weekday, each cell 15 minutes.
 * Paint "Working" for hours they take anything they deliver, or paint with a
 * bookable to reserve a block for it (tennis mornings, PT evenings) — a tagged
 * block is only offered for that bookable. The chosen week's leave and
 * one-offs are drawn on top; the pattern itself repeats every week.
 */

const STEP = 15;
const DAYS = [1, 2, 3, 4, 5, 6, 0]; // Mon … Sun
const DAY_NAME = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ROW_PX = 11;
// Tag colours for bookables (admin palette; the member apps never see these).
const TAG_STYLES = [
  'bg-sky-500 text-white', 'bg-violet-500 text-white', 'bg-amber-500 text-white', 'bg-rose-500 text-white', 'bg-teal-500 text-white', 'bg-fuchsia-500 text-white',
];
const WORK = 'any';

interface StandingLoad {
  weekStart: string;
  openIssues: number;
  series: Array<{ id: string; memberName: string; weekday: number; startTime: string; durationMin: number; endsOn: string | null }>;
  occurrences: Array<{ seriesId: string; date: string; startTime: string; durationMin: number; state: 'held' | 'booked'; memberName: string }>;
}
type Cell = string | null; // null = off · 'any' = working · 'id,id' = tagged

const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const toHHMM = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const addDays = (ymd: string, n: number) => { const d = new Date(`${ymd}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

const TEMPLATES: Array<{ label: string; spans: Array<[string, string]> }> = [
  { label: 'Mornings', spans: [['06:00', '12:00']] },
  { label: 'Evenings', spans: [['16:00', '21:00']] },
  { label: 'Split shift', spans: [['06:00', '10:00'], ['17:00', '21:00']] },
  { label: 'Full day', spans: [['06:00', '21:00']] },
];

export default function StaffAvailabilityTab({ mappingId }: { mappingId: string }) {
  const [branchId, setBranchId] = useState<string | null>(null);
  const [weekOf, setWeekOf] = useState<string | null>(null);
  const [data, setData] = useState<AvailabilityResponse | null>(null);
  const [grid, setGrid] = useState<Record<number, Cell[]>>({});
  const [saved, setSaved] = useState<string>('');
  const [brush, setBrush] = useState<string | null>(WORK); // null = eraser
  const [saving, setSaving] = useState(false);
  const painting = useRef<{ day: number; from: number; value: Cell } | null>(null);
  const [preview, setPreview] = useState<{ day: number; a: number; b: number } | null>(null);
  // Members' weekly standing slots with this provider — the load their hours carry.
  const [standing, setStanding] = useState<StandingLoad | null>(null);

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (branchId) q.set('branchId', branchId);
    if (weekOf) q.set('weekOf', weekOf);
    try {
      const r = await staffApi<AvailabilityResponse>(`/${mappingId}/availability?${q}`);
      setData(r);
      if (!branchId && r.branchId) setBranchId(r.branchId);
      const g = toGrid(r.windows, r.day);
      setGrid(g);
      setSaved(JSON.stringify(g));
    } catch (e: any) {
      toast.error(e.message);
    }
  }, [mappingId, branchId, weekOf]);

  useEffect(() => {
    const q = new URLSearchParams();
    if (branchId) q.set('branchId', branchId);
    if (weekOf) q.set('weekOf', weekOf);
    staffApi<StandingLoad>(`/${mappingId}/standing?${q.toString()}`).then(setStanding).catch(() => setStanding(null));
  }, [mappingId, branchId, weekOf]);
  useEffect(() => { load(); }, [load]);

  const dayStart = data ? toMin(data.day.start) : 360;
  const rows = data ? Math.max(1, Math.round((toMin(data.day.end) - dayStart) / STEP)) : 0;
  const dirty = JSON.stringify(grid) !== saved;
  const tagIndex = useMemo(() => new Map((data?.bookables ?? []).map((b, i) => [b.id, i])), [data]);

  function toGrid(windows: AvailabilityWindow[], day: { start: string; end: string }) {
    const start = toMin(day.start);
    const n = Math.max(1, Math.round((toMin(day.end) - start) / STEP));
    const g: Record<number, Cell[]> = {};
    for (const d of DAYS) g[d] = Array(n).fill(null);
    // Untagged first, tagged on top — a tagged block is exclusive, so it wins.
    const ordered = [...windows].sort((a, b) => a.classIds.length - b.classIds.length);
    for (const w of ordered) {
      const value = w.classIds.length ? [...w.classIds].sort().join(',') : WORK;
      const a = Math.max(0, Math.floor((toMin(w.startTime) - start) / STEP));
      const b = Math.min(n, Math.ceil((toMin(w.endTime) - start) / STEP));
      for (let i = a; i < b; i++) g[w.dayOfWeek][i] = value;
    }
    return g;
  }

  function toWindows(): AvailabilityWindow[] {
    const out: AvailabilityWindow[] = [];
    for (const d of DAYS) {
      const cells = grid[d] ?? [];
      let i = 0;
      while (i < cells.length) {
        const v = cells[i];
        if (!v) { i++; continue; }
        let j = i;
        while (j < cells.length && cells[j] === v) j++;
        out.push({ dayOfWeek: d, startTime: toHHMM(dayStart + i * STEP), endTime: toHHMM(dayStart + j * STEP), classIds: v === WORK ? [] : v.split(',') });
        i = j;
      }
    }
    return out;
  }

  const paintRange = (day: number, a: number, b: number, value: Cell) =>
    setGrid(g => {
      const cells = [...(g[day] ?? [])];
      for (let i = Math.min(a, b); i <= Math.max(a, b); i++) cells[i] = value;
      return { ...g, [day]: cells };
    });

  const onDown = (day: number, i: number) => {
    // Starting on a cell already painted with the brush erases instead — a natural toggle.
    const current = grid[day]?.[i] ?? null;
    const value = brush === null || current === brush ? null : brush;
    painting.current = { day, from: i, value };
    setPreview({ day, a: i, b: i });
  };
  const onEnter = (day: number, i: number) => {
    if (painting.current && painting.current.day === day) setPreview({ day, a: painting.current.from, b: i });
  };
  const onUp = () => {
    if (painting.current && preview) paintRange(preview.day, preview.a, preview.b, painting.current.value);
    painting.current = null;
    setPreview(null);
  };
  useEffect(() => {
    const up = () => onUp();
    window.addEventListener('pointerup', up);
    return () => window.removeEventListener('pointerup', up);
  });

  const applyTemplate = (spans: Array<[string, string]>, days: number[]) =>
    setGrid(g => {
      const next = { ...g };
      for (const d of days) {
        const cells: Cell[] = Array(rows).fill(null);
        for (const [s, e] of spans) {
          const a = Math.max(0, Math.floor((toMin(s) - dayStart) / STEP));
          const b = Math.min(rows, Math.ceil((toMin(e) - dayStart) / STEP));
          for (let i = a; i < b; i++) cells[i] = WORK;
        }
        next[d] = cells;
      }
      return next;
    });
  const copyMonday = () => setGrid(g => ({ ...g, 2: [...g[1]], 3: [...g[1]], 4: [...g[1]], 5: [...g[1]] }));

  const save = async () => {
    if (!branchId) return;
    setSaving(true);
    try {
      await staffApi(`/${mappingId}/availability`, { method: 'PUT', body: { branchId, windows: toWindows() } });
      setSaved(JSON.stringify(grid));
      toast.success('Hours saved');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-8"><div className="h-96 rounded-xl bg-zinc-100 animate-pulse" /></div>;
  if (!data.branches.length) return <div className="p-8 text-sm text-zinc-500">Give this team member access to a branch first (Edit member → branches).</div>;

  const cellClass = (v: Cell) => {
    if (!v) return '';
    if (v === WORK) return 'bg-lime-400';
    const idx = tagIndex.get(v.split(',')[0]) ?? 0;
    return TAG_STYLES[idx % TAG_STYLES.length].split(' ')[0];
  };
  const weekDates = DAYS.map((_, i) => addDays(data.weekStart, i));
  const hoursPerWeek = toWindows().reduce((s, w) => s + (toMin(w.endTime) - toMin(w.startTime)), 0) / 60;

  return (
    <div className="p-6 space-y-5 select-none">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {data.branches.length > 1 && (
          <select value={branchId ?? ''} onChange={e => setBranchId(e.target.value)} className="px-3 py-2 border border-zinc-200 rounded-xl text-sm font-semibold bg-white">
            {data.branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <BrushButton on={brush === WORK} onClick={() => setBrush(WORK)} swatch="bg-lime-400" label="Working" />
          {data.bookables.map((b, i) => (
            <BrushButton key={b.id} on={brush === b.id} onClick={() => setBrush(b.id)} swatch={TAG_STYLES[i % TAG_STYLES.length].split(' ')[0]} label={`${b.name} only`} />
          ))}
          <button onClick={() => setBrush(null)} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border ${brush === null ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-600 hover:border-zinc-400'}`}>
            <Eraser className="w-3.5 h-3.5" /> Erase
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-bold text-zinc-400 uppercase tracking-widest text-[10px]">Quick fill weekdays</span>
        {TEMPLATES.map(t => (
          <button key={t.label} onClick={() => applyTemplate(t.spans, [1, 2, 3, 4, 5])} className="px-2.5 py-1.5 rounded-lg border border-dashed border-zinc-300 font-semibold text-zinc-600 hover:border-zinc-900 hover:text-zinc-900">{t.label}</button>
        ))}
        <button onClick={copyMonday} className="px-2.5 py-1.5 rounded-lg border border-dashed border-zinc-300 font-semibold text-zinc-600 hover:border-zinc-900 hover:text-zinc-900">Copy Monday → Fri</button>
        <button onClick={() => applyTemplate([], DAYS)} className="px-2.5 py-1.5 rounded-lg font-semibold text-red-500 hover:bg-red-50">Clear week</button>
      </div>

      {/* Week navigator for overlays */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekOf(addDays(data.weekStart, -7))} className="p-1.5 rounded-lg hover:bg-zinc-100" aria-label="Previous week"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-bold text-zinc-700">
            Week of {new Date(`${data.weekStart}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </span>
          <button onClick={() => setWeekOf(addDays(data.weekStart, 7))} className="p-1.5 rounded-lg hover:bg-zinc-100" aria-label="Next week"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <span className="text-xs text-zinc-500 tabular-nums">{hoursPerWeek.toFixed(1)} h / week · repeats every week</span>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <div className="grid min-w-[640px]" style={{ gridTemplateColumns: `44px repeat(7, minmax(0, 1fr))` }}>
          <div />
          {DAYS.map((d, i) => {
            const leave = data.leaves.find(l => l.status === 'approved' && l.startDate <= weekDates[i] && l.endDate >= weekDates[i]);
            return (
              <div key={d} className="text-center pb-2">
                <div className="text-[11px] font-black text-zinc-900 uppercase tracking-widest">{DAY_NAME[d]}</div>
                <div className="text-[10px] text-zinc-400">{new Date(`${weekDates[i]}T00:00:00`).getDate()}</div>
                {leave && <div className="mt-1 mx-1 rounded bg-zinc-800 text-white text-[9px] font-bold py-0.5">{leave.isHalfDay ? 'Half-day leave' : 'On leave'}</div>}
              </div>
            );
          })}

          {/* Hour labels */}
          <div className="relative" style={{ height: rows * ROW_PX }}>
            {Array.from({ length: rows }, (_, i) => i).filter(i => (dayStart + i * STEP) % 60 === 0).map(i => (
              <div key={i} className="absolute right-2 text-[10px] text-zinc-400 tabular-nums -translate-y-1/2" style={{ top: i * ROW_PX }}>{toHHMM(dayStart + i * STEP)}</div>
            ))}
          </div>

          {DAYS.map((d, di) => {
            const date = weekDates[di];
            const blocks = data.blocks.filter(b => b.date === date);
            const leave = data.leaves.find(l => l.status === 'approved' && l.startDate <= date && l.endDate >= date && !l.isHalfDay);
            return (
              <div key={d} className="relative border-l border-zinc-100" style={{ height: rows * ROW_PX, touchAction: 'none' }}>
                {(grid[d] ?? []).map((v, i) => {
                  const inPreview = preview && preview.day === d && i >= Math.min(preview.a, preview.b) && i <= Math.max(preview.a, preview.b);
                  const shown = inPreview ? painting.current?.value ?? null : v;
                  const hour = (dayStart + i * STEP) % 60 === 0;
                  return (
                    <div
                      key={i}
                      onPointerDown={e => { (e.target as HTMLElement).releasePointerCapture?.(e.pointerId); onDown(d, i); }}
                      onPointerEnter={() => onEnter(d, i)}
                      className={`${hour ? 'border-t border-zinc-200' : 'border-t border-zinc-50'} cursor-crosshair ${cellClass(shown)} ${inPreview ? 'opacity-80' : ''}`}
                      style={{ height: ROW_PX }}
                    />
                  );
                })}
                {/* This week's exceptions */}
                {leave && <div className="absolute inset-0 bg-zinc-900/40 pointer-events-none" />}
                {/* Weekly standing slots this week — held (dashed) or booked (solid) */}
                {(standing?.occurrences ?? []).filter(o => o.date === date).map(o => {
                  const top = ((toMin(o.startTime) - dayStart) / STEP) * ROW_PX;
                  const h = (o.durationMin / STEP) * ROW_PX;
                  return (
                    <div key={`${o.seriesId}${o.date}`} className={`absolute left-1 right-1 rounded pointer-events-none text-[9px] font-bold px-1 overflow-hidden ${o.state === 'booked' ? 'bg-lime-300/90 text-zinc-900' : 'border-2 border-dashed border-lime-600 bg-lime-50/80 text-lime-900'}`}
                      style={{ top, height: Math.max(h, 12) }} title={`${o.memberName} · weekly ${o.startTime} (${o.state})`}>
                      {o.memberName}
                    </div>
                  );
                })}
                {blocks.map(b => {
                  const top = ((toMin(b.startTime) - dayStart) / STEP) * ROW_PX;
                  const h = ((toMin(b.endTime) - toMin(b.startTime)) / STEP) * ROW_PX;
                  const extra = b.kind === 'override_open';
                  return (
                    <div key={b.id} className={`absolute left-0.5 right-0.5 rounded pointer-events-none text-[9px] font-bold px-1 overflow-hidden ${extra ? 'border-2 border-emerald-500 text-emerald-800 bg-emerald-50/70' : 'bg-red-500/80 text-white'}`} style={{ top, height: Math.max(h, 12) }} title={b.reason ?? b.kind}>
                      {extra ? 'Extra' : b.reason || b.kind}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {standing && standing.series.length > 0 && (
        <div className="rounded-xl border border-lime-200 bg-lime-50/40 p-3 space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-widest text-lime-800">
              Weekly slots · {standing.series.length} · {Math.round(standing.series.reduce((m, x) => m + x.durationMin, 0) / 6) / 10} h a week
            </p>
            {standing.openIssues > 0 && <span className="text-[10px] font-black uppercase tracking-widest text-rose-600">{standing.openIssues} to sort out</span>}
          </div>
          {standing.series.map(x => (
            <div key={x.id} className="flex items-center justify-between text-xs">
              <span className="font-semibold text-zinc-800 truncate">{x.memberName}</span>
              <span className="text-zinc-500 tabular-nums">{DAY_NAME[x.weekday]} {x.startTime} · {x.durationMin} min{x.endsOn ? ` · to ${x.endsOn.slice(8, 10)}/${x.endsOn.slice(5, 7)}` : ''}</span>
            </div>
          ))}
          <p className="text-[10px] text-zinc-500">Held on the grid even before they are booked. Removing hours under a slot doesn't move it — the member's weeks are flagged for you instead.</p>
        </div>
      )}

      <OneOffs mappingId={mappingId} branchId={branchId} weekStart={data.weekStart} blocks={data.blocks} onChange={load} />

      <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-white/90 backdrop-blur border-t border-zinc-100 flex items-center justify-between gap-2">
        <span className="text-xs text-zinc-500">Approved leave hides them from booking and checkout automatically.</span>
        <div className="flex gap-2">
          <button onClick={() => setGrid(JSON.parse(saved))} disabled={!dirty || saving} className="px-4 py-2.5 rounded-xl text-sm font-bold text-zinc-600 hover:bg-zinc-100 disabled:opacity-40">Discard</button>
          <button onClick={save} disabled={!dirty || saving} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-zinc-900 text-white hover:bg-black disabled:opacity-40">{saving ? 'Saving…' : 'Save hours'}</button>
        </div>
      </div>
    </div>
  );
}

function BrushButton({ on, onClick, swatch, label }: { on: boolean; onClick: () => void; swatch: string; label: string }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border max-w-[200px] ${on ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-600 hover:border-zinc-400'}`}>
      {on ? <Paintbrush className="w-3.5 h-3.5 shrink-0" /> : <span className={`w-3 h-3 rounded-sm shrink-0 ${swatch}`} />}
      <span className="truncate">{label}</span>
    </button>
  );
}

const KIND_LABEL: Record<string, string> = { break: 'Break', external: 'Away', leave: 'Leave', override_open: 'Extra shift' };

/** One-off changes to a single date: a break, time away, or an extra shift. */
function OneOffs({ mappingId, branchId, weekStart, blocks, onChange }: {
  mappingId: string; branchId: string | null; weekStart: string; blocks: AvailabilityResponse['blocks']; onChange: () => void;
}) {
  const [draft, setDraft] = useState({ date: weekStart, startTime: '13:00', endTime: '14:00', kind: 'break', reason: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(d => ({ ...d, date: weekStart })), [weekStart]);

  const add = async () => {
    setBusy(true);
    try {
      await staffApi(`/${mappingId}/blocks`, { method: 'POST', body: { ...draft, branchId } });
      onChange();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    try { await staffApi(`/${mappingId}/blocks/${id}`, { method: 'DELETE' }); onChange(); } catch (e: any) { toast.error(e.message); }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 p-4 space-y-3">
      <div className="text-sm font-black text-zinc-900">One-offs this week</div>
      {blocks.length === 0 && <p className="text-xs text-zinc-400">Nothing different this week.</p>}
      {blocks.map(b => (
        <div key={b.id} className="flex items-center justify-between gap-3 text-sm">
          <span className="min-w-0 truncate">
            <b>{new Date(`${b.date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</b>
            {' · '}{b.startTime}–{b.endTime} · {KIND_LABEL[b.kind] ?? b.kind}{b.reason ? ` · ${b.reason}` : ''}
          </span>
          <button onClick={() => remove(b.id)} className="p-1.5 text-zinc-400 hover:text-red-500" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2 pt-1">
        <input type="date" value={draft.date} onChange={e => setDraft({ ...draft, date: e.target.value })} className="px-2 py-2 border border-zinc-200 rounded-lg text-sm" />
        <input type="time" value={draft.startTime} step={900} onChange={e => setDraft({ ...draft, startTime: e.target.value })} className="px-2 py-2 border border-zinc-200 rounded-lg text-sm" />
        <input type="time" value={draft.endTime} step={900} onChange={e => setDraft({ ...draft, endTime: e.target.value })} className="px-2 py-2 border border-zinc-200 rounded-lg text-sm" />
        <select value={draft.kind} onChange={e => setDraft({ ...draft, kind: e.target.value })} className="px-2 py-2 border border-zinc-200 rounded-lg text-sm bg-white">
          <option value="break">Break</option>
          <option value="external">Away</option>
          <option value="override_open">Extra shift</option>
        </select>
        <input placeholder="Note (optional)" value={draft.reason} onChange={e => setDraft({ ...draft, reason: e.target.value })} className="flex-1 min-w-[140px] px-2 py-2 border border-zinc-200 rounded-lg text-sm" />
        <button onClick={add} disabled={busy} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-zinc-900 text-white text-sm font-bold disabled:opacity-40"><Plus className="w-4 h-4" /> Add</button>
      </div>
    </div>
  );
}
